import 'remote-web-worker';

import { message } from 'antd';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import Button from '@/components/common/Button';
import { RightContext } from '@/components/RightProvider/context';
import { usePartial } from '@/hooks/usePartial';
import UserStore, { CurrentVip } from '@/store/User';
import { AIFunction, CLONE_TEXT } from '@/utils/const';
import { isCnArea } from '@/utils/envHelper';
import { t } from '@/utils/helper/intl';

import CloseIcon from '../icon/close';
import CommonModal, { PaymentModal } from '../index';
import { createAvatarByImage, createAvatarByVideo } from '../service';
import AvatarForm, { AvatarFormData, defaultAvatarData } from './components/avatarForm';
import DraggerContent, {
  DraggerContentRef,
  ImageUrlData,
  UrlData,
  VideoUrlData,
} from './components/draggerContent';
import Guide from './components/guide';
import {
  DefaultAvatarPrompt,
  handleTextVerify,
  NegativePromptSuffix,
} from './components/utils';

export type AvatarMode = 'video' | 'image' | '';
export type From = 'avatar_video' | 'avatar' | 'workspace';
interface CreateAvatarModal {
  onClose?: () => void;
  onConfirm?: () => void;
  mode?: AvatarMode;
  from: From;
}
function isAvailable(from: From, currentVip: CurrentVip, cloneVoice?: boolean) {
  try {
    if (!currentVip) {
      console.error('currentVip无法获取');
      return false;
    }
    if (
      currentVip?.avatarSeat?.available <= 0 ||
      (cloneVoice && currentVip?.voiceSeat?.available <= 0)
    ) {
      // let msg = '';
      // if (cloneVoice && currentVip?.voiceSeat?.available <= 0) {
      //   msg = t('merror.sub.noseatvoice');
      // } else {
      //   msg = t('merror.sub.noseat');
      // }
      PaymentModal.show({
        feature: AIFunction.Other,
        // customTip: msg,
        onOk() {
          UserStore.getState().getUserBenefit(true);
        },
      });
      return false;
    }
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

const Content = (props: CreateAvatarModal) => {
  const { onClose, onConfirm, mode = '', from } = props;
  const [avatarMode, setAvatarMode] = useState<AvatarMode>(mode);
  const rightContext = useContext(RightContext);
  const [btnState, setBtnState] = usePartial({
    disabled: true,
    loading: false,
  });
  const formData = useRef<AvatarFormData>(defaultAvatarData);
  const urlData = useRef<UrlData>({
    ...{
      file: null,
      format: '',
    },
    ...(avatarMode === 'video'
      ? { videoUrl: '', previewUrl: '', duration: '', resolution: '' }
      : { previewUrl: '' }),
  });
  const draggerRef = useRef<DraggerContentRef>(null);
  useEffect(() => {
    rightContext.withUpdate();
  }, []);

  const onCancel = () => {
    props.onClose?.();
  };
  const handleConfirm = async () => {
    if (!formData.current.avatarName) {
      message.warning(t('please-enter-a-name'));
      return;
    }
    if (!formData.current.gender) {
      message.warning(t('please-check-a-gender'));
      return;
    }

    try {
      setBtnState({ loading: true });
      let isValid = isAvailable(
        from,
        rightContext?.currentVip,
        formData.current.voiceClone,
      );
      if (!isValid) {
        return;
      }

      if (avatarMode === 'image' && isCnArea) {
        isValid = await handleTextVerify(formData.current.prompt);
      }
      if (!isValid) {
        return;
      }

      let params: any = {};
      if (avatarMode === 'video') {
        const data = urlData.current as VideoUrlData;
        params = {
          videoUrl: data.videoUrl,
          title: formData.current.avatarName,
          previewUrl: urlData.current.previewUrl,
          gender: formData.current.gender,
          voiceClone: formData.current.voiceClone,
        };
        if (formData.current.voiceClone) {
          params.voiceCloneType = 6;
          params.voicePreviewText = CLONE_TEXT[Math.floor(Math.random() * 10)];
        }
      } else {
        params = {
          imgUrl: (urlData.current as ImageUrlData).previewUrl,
          avatarName: formData.current.avatarName,
          gender: formData.current.gender,
          // prompt: formData.current.prompt || DefaultAvatarPrompt,
          prompt:
            DefaultAvatarPrompt + formData.current.prompt
              ? `\nThe special requirements of users are as follows:\n${formData.current.prompt}\nPlease generate a video based on the basic requirements and the user's special requirements`
              : '',
          negative_prompt: formData.current.negative_prompt + NegativePromptSuffix,
        };
      }

      const result =
        avatarMode === 'video'
          ? await createAvatarByVideo(params)
          : await createAvatarByImage(params);

      if (result.success) {
        message.success(t('merror.tip.submitSuccess'));
        rightContext.withUpdate();
        onConfirm?.();
      } else {
        message.error(result.message);
      }
    } finally {
      setBtnState({ loading: false });
    }
  };
  const getDraggerData = (data: UrlData) => {
    urlData.current = data;

    if (avatarMode === 'video') {
      setBtnState({
        disabled: !(data as VideoUrlData).videoUrl,
      });
    } else {
      setBtnState({
        disabled: !(data as ImageUrlData).previewUrl,
      });
    }
  };
  const handleClear = () => {
    urlData.current = {
      file: null,
      videoUrl: '',
      previewUrl: '',
      format: '',
      resolution: '',
      duration: '',
    };
    if (avatarMode === 'video') {
      setBtnState({
        disabled: !urlData.current.videoUrl,
      });
    } else {
      setBtnState({
        disabled: !urlData.current.previewUrl,
      });
    }
  };

  const changeForm = useCallback((data: AvatarFormData) => {
    formData.current = data;

    if (avatarMode === 'video') {
      setBtnState({
        disabled: !(urlData.current as VideoUrlData).videoUrl,
      });
    } else {
      setBtnState({
        disabled: !(urlData.current as ImageUrlData).previewUrl,
      });
    }
  }, []);
  if (avatarMode === '') {
    return (
      <Guide
        onClose={onClose}
        onChoose={(mode) => {
          setAvatarMode(mode);
        }}
      />
    );
  }
  return (
    <div className="flex flex-col">
      <Title onClose={onClose} />
      <DraggerContent
        t={t}
        ref={draggerRef}
        onChange={getDraggerData}
        onClear={handleClear}
        mode={avatarMode as Exclude<AvatarMode, ''>}
      />
      <AvatarForm
        duration={(urlData.current as VideoUrlData).duration}
        url={urlData.current.previewUrl}
        onChange={changeForm}
        mode={avatarMode as Exclude<AvatarMode, ''>}
      />
      <div className="w-full h-[64px] flex items-center justify-center">
        <div className="flex flex-row items-center gap-[8px]">
          <Button
            onClick={onCancel}
            style={{
              border: '1px solid var(--app-border)',
              background: 'var(--app-bg-card)',
              color: 'var(--app-text-primary)',
            }}
            className="!font-[500] !px-[35px] min-w-[142px]"
          >
            {t('merror.operate.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            style={{
              background: '#5661F1',
            }}
            className="!font-normal !px-[35px]  min-w-[142px]"
            disabled={btnState.disabled}
            loading={btnState.loading}
          >
            {t('merror.operate.confirm')}
          </Button>
        </div>
      </div>
    </div>
  );
};
const Title: React.FC<{ onClose?: () => void }> = (props) => {
  const { onClose } = props;
  const currentVip = UserStore((store) => store.currentVip);
  return (
    <div className="h-[56px] px-[16px] flex flex-row items-center justify-center relative ">
      <span className="cursor-pointer absolute top-[18px] right-[10px]" onClick={onClose}>
        <CloseIcon />
      </span>
      <span className="text-[20px] font-[500] text-[#fff] pr-[8px]">{t('bc.ac')}</span>
      <span className="bg-common-modal-seat-tag-bg px-[8px] inline-block rounded-[4px] text-[12px] text-common-modal-seat-tag-color">
        <span className="font-number">
          {currentVip.avatarSeat.used}/{currentVip.avatarSeat.total}
        </span>
        &nbsp;{t('used')}
      </span>
    </div>
  );
};
export function createAvatarModalShow(options: CreateAvatarModal = { from: 'avatar' }) {
  CommonModal.show((t) => ({
    width: 690,
    // title: <Title />,
    footer: false,
    customPadding: {
      x: 16,
      y: 0,
    },
    content: (close) => (
      <Content
        {...options}
        onConfirm={() => {
          close();
          options.onConfirm?.();
        }}
        onClose={() => {
          close();
          options.onClose?.();
        }}
      />
    ),
  }));
}
